import dayjs, { Dayjs } from 'dayjs';

import { Entitlement } from 'app/services/GuestCenterService.types';

export const DATE_FORMAT_YMD = 'YYYY-MM-DD';
export const DATE_FORMAT_ISO = 'YYYY-MM-DDTHH:mm:ss';
export const DATE_FORMAT_TIME_TZ = 'hh:mm A z';

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const nextTick = () => sleep(0);

type TimeInterval = 's' | 'm' | 'h' | 'd';

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const timeIntervals: Record<TimeInterval, number> = {
  s: SECOND,
  m: MINUTE,
  h: HOUR,
  d: DAY,
};

export const toTimezoneAbbreviation = (timezone?: string, time?: Dayjs): string => {
  if (!timezone) return '';
  return dayjs(time).tz(timezone).format('z');
};

export const toMs = (unit: number, interval: TimeInterval): number => {
  const ms = timeIntervals[interval];
  if (!interval) throw new Error(`unsupported interval: ${interval}`);
  return unit * ms;
};

export const toYMD = (isoDate = '') => isoDate.substring(0, 10);

export const isOlderThan = (timestamp: number, durationMs: number): boolean => {
  return timestamp < Date.now() - durationMs;
};

type Unsubscribe = () => void;
export const onTimeout = (callback: () => void, ms: number): Unsubscribe => {
  const t = setTimeout(callback, ms);
  return () => clearTimeout(t);
};

export const isPassExpired = (validTo: string): boolean => {
  return dayjs(validTo).isBefore(dayjs());
};

export const isPassExpiredWithinOneWeek = (validTo: string): boolean => {
  const expiration = dayjs(validTo);
  const now = dayjs();
  const oneWeekAgo = now.subtract(7, 'day');

  return expiration.isBefore(now) && expiration.isAfter(oneWeekAgo);
};

export const areAllPassesExpiredWithinOneWeek = (entitlement?: Entitlement | null): boolean => {
  if (!entitlement) {
    return false;
  }

  const { passes } = entitlement;

  if (!passes || passes.length === 0) {
    return false;
  }

  return passes.every((pass) => isPassExpiredWithinOneWeek(pass.validTo));
};

export const areAllPassesExpired = (entitlement: Entitlement): boolean => {
  const { passes } = entitlement;

  if (!passes || passes.length === 0) {
    return false;
  }

  return passes.every((pass) => isPassExpired(pass.validTo));
};

export const isEntitlementSelectable = (entitlement: Entitlement): boolean => {
  if (entitlement.isValid) return true;
  const passesExpiredCheck = areAllPassesExpiredWithinOneWeek(entitlement);
  return passesExpiredCheck;
};
